/**
 * gameStorePristine — 12/02/2026
 *
 * Snapshot des valeurs initiales de tous les slices Zustand, capturé une
 * seule fois au bootstrap (avant toute persist rehydration). Utilisé par
 * `resetGame()` et `createCompany()` pour garantir un state pristine sur
 * TOUS les champs data définis par les slices, y compris ceux qui ne
 * sont pas explicitement listés dans `helpers/resetGameState.js`.
 *
 * Retour user prod : « une nouvelle partie arrivé en avril les anciens
 * chauffeurs de la partie précédente apparaissent dans la nouvelle partie ».
 * Cause : Zustand `set()` fait un merge, donc les champs comme
 * `driverPool`, `hiredSupport`, `pendingMarchettiBonus`, etc. définis
 * uniquement dans leurs slice creators restaient tels quels entre parties.
 *
 * Le snapshot est capturé en exécutant chaque slice creator avec des
 * `set`/`get` no-op qui ne font que collecter les valeurs initiales. C'est
 * une opération pure et déterministe → aucun side-effect.
 */
import { createCoreSlice } from './slices/coreSlice';
import { createFleetSlice } from './slices/fleetSlice';
import { createHRSlice } from './slices/hrSlice';
import { createContractSlice } from './slices/contractSlice';
import { createFinanceSlice } from './slices/financeSlice';
import { createEventSlice } from './slices/eventSlice';
import { createUISlice } from './slices/uiSlice';
import { createTaxSlice } from './slices/taxSlice';
import { createBankSlice } from './slices/bankSlice';
import { createSupportSlice } from './slices/supportSlice';
import { createMarketIntelSlice } from './slices/marketIntelSlice';
import { createAuditSlice } from './slices/auditSlice';
import { createMessageSlice } from './slices/messageSlice';
import { createAcquisitionSlice } from './slices/acquisitionSlice';
import { createCrmSlice } from './slices/crmSlice';
import { createOfficeSlice } from './slices/officeSlice';
import { createLeaseAidSlice } from './slices/leaseAidSlice';
import { createAchievementsSlice } from './slices/achievementsSlice';
import { createTenderSlice } from './slices/tenderSlice';
import { createSpotEngagementSlice } from './slices/spotEngagementSlice';
import { createCommercialOpportunitySlice } from './slices/commercialOpportunitySlice';
import { createDepotManagerSlice } from './slices/depotManagerSlice';
import { createContractHistorySlice } from './slices/contractHistorySlice';
import { createRelationEventsSlice } from './slices/relationEventsSlice';
import { createLegalProceduresSlice } from './slices/legalProceduresSlice';
import { createDriverPoolSlice } from './slices/driverPoolSlice';
import { createConvoyExceptionnelSlice } from './slices/convoyExceptionnelSlice';
import { createWorkAccidentSlice } from './slices/workAccidentSlice';
import { createMarketingSalonSlice } from './slices/marketingSalonSlice';
import { createPedagogySlice } from './slices/pedagogySlice';
import { createScenarioPushSlice } from './slices/scenarioPushSlice';

let _pristineCache = null;

export function getPristineStore() {
  if (_pristineCache) return deepClone(_pristineCache);

  const noopSet = () => {};
  const noopGet = () => ({});
  const noopApi = { setState: noopSet, getState: noopGet };
  const args = [noopSet, noopGet, noopApi];

  try {
    _pristineCache = {
      ...createCoreSlice(...args),
      ...createFleetSlice(...args),
      ...createHRSlice(...args),
      ...createContractSlice(...args),
      ...createFinanceSlice(...args),
      ...createEventSlice(...args),
      ...createUISlice(...args),
      ...createTaxSlice(...args),
      ...createBankSlice(...args),
      ...createSupportSlice(...args),
      ...createMarketIntelSlice(...args),
      ...createAuditSlice(...args),
      ...createMessageSlice(...args),
      ...createAcquisitionSlice(...args),
      ...createCrmSlice(...args),
      ...createOfficeSlice(...args),
      ...createLeaseAidSlice(...args),
      ...createAchievementsSlice(...args),
      ...createTenderSlice(...args),
      ...createSpotEngagementSlice(...args),
      ...createCommercialOpportunitySlice(...args),
      ...createDepotManagerSlice(...args),
      ...createContractHistorySlice(...args),
      ...createRelationEventsSlice(...args),
      ...createLegalProceduresSlice(...args),
      ...createDriverPoolSlice(...args),
      ...createConvoyExceptionnelSlice(...args),
      ...createWorkAccidentSlice(...args),
      ...createMarketingSalonSlice(...args),
      ...createPedagogySlice(...args),
      ...createScenarioPushSlice(...args),
    };
  } catch (err) {
    console.warn('[gameStorePristine] capture failed:', err);
    return null;
  }
  return deepClone(_pristineCache);
}

function deepClone(obj) {
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return obj;
  }
}
